// Working rules — spec ch.06: "Separate booking/matching/message toggles,
// amount limit, feedback, pause and release path … Immutable policy versions,
// candidate rules, test evidence, independent office approval and per-operation
// release. Never use editable browser switches as authority."
//
// The last sentence is the whole design of this screen. There are no toggles,
// and nothing that behaves like one: every control on this page writes a
// RECORD — a decision with an actor and a time, a customer-approved mapping, a
// piece of test evidence, a release bound to what was tested.
//
// ---------------------------------------------------------------------------
// Five blocks, each one a thing that exists in this system
// ---------------------------------------------------------------------------
//
//   1. BOOKING RULES. Which relation and ledger each supplier books to: the
//      supplier rules in the administration's booking rules, as the worker
//      reads them (GET /supplier-rules). Read-only — a rule is added from the
//      document that needs one (POST /documents/{id}/supplier-rule).
//   2. COUNTERPARTY QUESTIONS. Which parties are still waiting on somebody, and
//      what the office has already decided.
//   3. INTAKE ROUTES. Where documents come in from. ch.02 calls a route "a
//      customer-approved mapping", which is why creating one is the owner's.
//   4. THE ROUTING QUEUE. Arrivals nobody could route. Visible only to an
//      operator holding the triage grant, and this screen says so plainly to
//      everybody else rather than showing an empty list.
//   5. AUTOMATION POLICIES. The four release gates, in order, with each one's
//      button offered only to the role that may press it.
//
// A released policy does not make anything run by itself. Nothing in this
// system consults a release before approving something. The API reports
// `inForce: false` on every release for that reason, and this screen prints it.
//
// Sections load and fail independently: a reviewer whose triage read answers
// 404 still sees their counterparty questions. Nothing here blanks the page.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, newIdempotencyKey, request, tenantPath } from '../api/client.ts';
import type {
  Administration,
  AdministrationSettings,
  Classified,
  CounterpartyClassifications,
  IntakeRoute,
  IntakeRoutes,
  PolicyVersion,
  PolicyVersions,
  ProviderRef,
  SupplierRules,
  SupplierRuleView,
  TriageQueue,
} from '../api/types.ts';
import {
  ClassifyCounterparty,
  type CounterpartyClassification,
} from '../components/ClassifyCounterparty.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { InfoTip } from '../components/InfoTip.tsx';
import { RowMenu } from '../components/RowMenu.tsx';
import { PROVIDER_NAMES } from '../components/ActorAvatar.tsx';
import type { ScreenId } from '../components/Shell.tsx';
import { ALL_ADMINISTRATIONS, administrationFilter, useWorkspace } from '../state/workspace.tsx';
import { useT, type MessageKey, type Translate } from '../i18n/index.tsx';
import * as format from '../format.ts';

/** What a section knows. `error` and `data` are separate so a reload can fail
 *  without throwing away what is on screen. */
type Section<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly data: T };

/** ch.02's four release scopes. There is no 'everything' — see app.release_scope. */
const SCOPES = [
  'standard_purchase_booking',
  'standard_sales_booking',
  'exact_bank_matching',
  'standard_missing_document_requests',
] as const;

function scopeLabel(value: string, t: Translate): string {
  return (SCOPES as readonly string[]).includes(value)
    ? t(`rules.scope.${value}` as MessageKey)
    : value.replace(/_/g, ' ');
}

/** Which gate is open: its tone. Words come from rules.gate.*. */
const GATE_TONE: Readonly<Record<string, string>> = {
  test_evidence: 'warn',
  office_approval: 'warn',
  release: 'blue',
  released: 'ok',
};

const CHANNELS = ['mailbox', 'upload', 'webhook'] as const;

function channelLabel(value: string, t: Translate): string {
  return (CHANNELS as readonly string[]).includes(value) ? t(`rules.channel.${value}` as MessageKey) : value;
}

const UNROUTED = ['no_route', 'route_revoked', 'ambiguous_source'] as const;

/** ch.02: a route is "a customer-approved mapping", so only the owner writes one. */
const ROUTE_APPROVER = new Set(['owner']);
/** Filing and dismissing an arrival. The triage GRANT is separate and the API decides it. */
const TRIAGE_ROLES = new Set(['owner', 'reviewer', 'agency_operator']);
const POLICY_REQUESTER = new Set(['owner']);
const POLICY_APPROVER = new Set(['owner']);
const POLICY_OPERATOR = new Set(['agency_operator']);

export function WorkingRulesScreen({
  onOpenDocument,
  onNavigate,
}: {
  readonly onOpenDocument: (documentId: string) => void;
  readonly onNavigate?: (screen: ScreenId) => void;
}): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const { token, administrations, administrationId } = workspace;
  const tenantId = workspace.membership.tenantId;
  const role = workspace.membership.role;
  const filter = administrationFilter(administrationId);

  const [bookingRules, setBookingRules] = useState<Section<SupplierRules>>({ kind: 'loading' });
  const [counterparties, setCounterparties] = useState<Section<CounterpartyClassifications>>({
    kind: 'loading',
  });
  const [routes, setRoutes] = useState<Section<IntakeRoutes>>({ kind: 'loading' });
  const [queue, setQueue] = useState<Section<TriageQueue>>({ kind: 'loading' });
  const [policies, setPolicies] = useState<Section<PolicyVersions>>({ kind: 'loading' });
  const [settings, setSettings] = useState<Section<readonly AdministrationSettings[]>>({ kind: 'loading' });
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const query = filter === undefined ? '' : `?administrationId=${encodeURIComponent(filter)}`;

  const loadBookingRules = useCallback(async () => {
    try {
      setBookingRules({
        kind: 'ready',
        data: await request<SupplierRules>(tenantPath(tenantId, `/supplier-rules${query}`), { token }),
      });
    } catch (caught) {
      setBookingRules({ kind: 'error', message: sectionMessage(caught, t) });
    }
  }, [tenantId, token, query, t]);

  const loadCounterparties = useCallback(async () => {
    try {
      setCounterparties({
        kind: 'ready',
        data: await request<CounterpartyClassifications>(
          tenantPath(tenantId, `/counterparty-classifications${query}`),
          { token },
        ),
      });
    } catch (caught) {
      setCounterparties({ kind: 'error', message: sectionMessage(caught, t) });
    }
  }, [tenantId, token, query, t]);

  const loadRoutes = useCallback(async () => {
    try {
      setRoutes({
        kind: 'ready',
        data: await request<IntakeRoutes>(tenantPath(tenantId, '/intake-routes'), { token }),
      });
    } catch (caught) {
      setRoutes({ kind: 'error', message: sectionMessage(caught, t) });
    }
  }, [tenantId, token, t]);

  const loadQueue = useCallback(async () => {
    try {
      setQueue({
        kind: 'ready',
        data: await request<TriageQueue>(tenantPath(tenantId, '/triage/arrivals'), { token }),
      });
    } catch (caught) {
      setQueue({ kind: 'error', message: sectionMessage(caught, t) });
    }
  }, [tenantId, token, t]);

  const loadPolicies = useCallback(async () => {
    try {
      setPolicies({
        kind: 'ready',
        data: await request<PolicyVersions>(tenantPath(tenantId, `/policy-versions${query}`), {
          token,
        }),
      });
    } catch (caught) {
      setPolicies({ kind: 'error', message: sectionMessage(caught, t) });
    }
  }, [tenantId, token, query, t]);

  // The administrations "Bank en kas" describes: the selected one, or all of them.
  const settingsIds = useMemo(
    () => (filter === undefined ? administrations.map((administration) => administration.id) : [filter]),
    [filter, administrations],
  );
  const settingsKey = settingsIds.join(',');

  const loadSettings = useCallback(async () => {
    try {
      const all = await Promise.all(
        settingsIds.map((id) =>
          request<AdministrationSettings>(tenantPath(tenantId, `/administrations/${encodeURIComponent(id)}/settings`), { token }),
        ),
      );
      setSettings({ kind: 'ready', data: all });
    } catch (caught) {
      setSettings({ kind: 'error', message: sectionMessage(caught, t) });
    }
    // settingsKey stands for settingsIds: the same administrations are the same read.
  }, [tenantId, token, settingsKey, t]);

  useEffect(() => {
    setSettings({ kind: 'loading' });
    void loadSettings();
    // Keyed on what is read, as below: a language switch must not re-read it.
  }, [tenantId, token, settingsKey]);

  useEffect(() => {
    // Reset first. Another office's routes must not stay on screen under the
    // new office's name while the next request is in flight.
    setBookingRules({ kind: 'loading' });
    setCounterparties({ kind: 'loading' });
    setRoutes({ kind: 'loading' });
    setQueue({ kind: 'loading' });
    setPolicies({ kind: 'loading' });
    setNotice(null);
    void loadBookingRules();
    void loadCounterparties();
    void loadRoutes();
    void loadQueue();
    void loadPolicies();
    // Keyed on what is read, not on the loaders: they also change with the
    // language (their messages are translated), and a language switch must not
    // re-read every section.
  }, [tenantId, token, query]);

  /** Every mutation on this screen runs through here: one busy flag, one notice. */
  const act = useCallback(
    async (work: () => Promise<string>): Promise<void> => {
      setBusy(true);
      setNotice(null);
      try {
        setNotice(await work());
      } catch (caught) {
        setNotice(actionMessage(caught, t));
      } finally {
        setBusy(false);
      }
    },
    [t],
  );

  const classify = useCallback(
    (documentId: string, sourceVersion: number, classification: CounterpartyClassification) =>
      act(async () => {
        const answered = await request<Classified>(
          tenantPath(tenantId, `/documents/${encodeURIComponent(documentId)}/classify-counterparty`),
          {
            method: 'POST',
            token,
            idempotencyKey: newIdempotencyKey(),
            // The version the LIST reported. A document re-read since then
            // answers 409, and the message says so rather than retrying on
            // whatever is current — the reviewer answered about the version
            // they were shown.
            body: { expectedRevision: sourceVersion, classification },
          },
        );
        await loadCounterparties();
        return answered.classification === 'one_off' ? t('rules.cp.recordedOneOff') : t('rules.cp.recordedRecurring');
      }),
    [act, tenantId, token, loadCounterparties, t],
  );

  const createRoute = useCallback(
    (body: Record<string, unknown>) =>
      act(async () => {
        await request<{ route: IntakeRoute }>(tenantPath(tenantId, '/intake-routes'), {
          method: 'POST',
          token,
          idempotencyKey: newIdempotencyKey(),
          body,
        });
        await loadRoutes();
        return t('rules.routes.created');
      }),
    [act, tenantId, token, loadRoutes, t],
  );

  const revokeRoute = useCallback(
    (routeId: string) =>
      act(async () => {
        await request(
          tenantPath(tenantId, `/intake-routes/${encodeURIComponent(routeId)}/revoke`),
          { method: 'POST', token, idempotencyKey: newIdempotencyKey(), body: {} },
        );
        await loadRoutes();
        return t('rules.routes.revokedNotice');
      }),
    [act, tenantId, token, loadRoutes, t],
  );

  const fileArrival = useCallback(
    (arrivalId: string, adminId: string) =>
      act(async () => {
        await request(
          tenantPath(tenantId, `/triage/arrivals/${encodeURIComponent(arrivalId)}/route`),
          {
            method: 'POST',
            token,
            idempotencyKey: newIdempotencyKey(),
            body: { administrationId: adminId },
          },
        );
        await loadQueue();
        return t('rules.triage.filed');
      }),
    [act, tenantId, token, loadQueue, t],
  );

  const dismissArrival = useCallback(
    (arrivalId: string, reason: string) =>
      act(async () => {
        await request(
          tenantPath(tenantId, `/triage/arrivals/${encodeURIComponent(arrivalId)}/dismiss`),
          { method: 'POST', token, idempotencyKey: newIdempotencyKey(), body: { reason } },
        );
        await loadQueue();
        return t('rules.triage.dismissed');
      }),
    [act, tenantId, token, loadQueue, t],
  );

  const policyAction = useCallback(
    (path: string, body: Record<string, unknown>, message: string) =>
      act(async () => {
        await request(tenantPath(tenantId, path), {
          method: 'POST',
          token,
          idempotencyKey: newIdempotencyKey(),
          body,
        });
        await loadPolicies();
        return message;
      }),
    [act, tenantId, token, loadPolicies],
  );

  return (
    <div className="rules" data-testid="working-rules">
      <header className="page-head">
        <h1>{t('rules.title')}</h1>
        <p>{t('rules.intro')}</p>
      </header>

      {notice === null ? null : (
        <div className="notice" role="status" data-testid="rules-notice">
          <div className="grow">{notice}</div>
        </div>
      )}

      <PolicySection
        state={policies}
        administrations={administrations}
        administrationId={administrationId}
        role={role}
        busy={busy}
        onAction={policyAction}
      />

      {/* Two columns: what comes in and what is set on the left, the booking
          rules (the longest list) on the right. Each column stacks on its own,
          so a short card never leaves a hole beside a long one. */}
      <div className="rules-grid">
        <div className="rules-col">
          <IntakeRouteSection
            state={routes}
            administrations={administrations}
            role={role}
            busy={busy}
            mayApprove={ROUTE_APPROVER.has(role)}
            currentUserId={workspace.me.user.id}
            onCreate={createRoute}
            onRevoke={revokeRoute}
          />

          <CounterpartySection
            state={counterparties}
            administrations={administrations}
            busy={busy}
            onClassify={classify}
            onOpenDocument={onOpenDocument}
          />

          <BankAndCashSection state={settings} administrations={administrations} />

          <TriageSection
            state={queue}
            administrations={administrations}
            busy={busy}
            mayAct={TRIAGE_ROLES.has(role)}
            role={role}
            onFile={fileArrival}
            onDismiss={dismissArrival}
          />
        </div>

        <div className="rules-col">
          <BookingRulesSection
            state={bookingRules}
            administrations={administrations}
            onNavigate={onNavigate}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The block every section is drawn in.
// ---------------------------------------------------------------------------

function Block({
  icon,
  tone,
  title,
  info,
  action,
  testId,
  children,
}: {
  readonly icon: IconName;
  readonly tone: string;
  readonly title: string;
  readonly info?: ReactNode;
  readonly action?: ReactNode;
  readonly testId?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section className="dash-card rule-block" data-testid={testId}>
      <div className="rule-head">
        <span className={`tile rule-tile ${tone}`} aria-hidden="true">
          <Icon name={icon} size={20} />
        </span>
        {/* Title and button share a line while the title fits in two; on a
            narrow card the button wraps under the title. */}
        <div className="rule-head-main">
          <h2>
            <TitleWithInfo title={title} info={info} />
          </h2>
          {action}
        </div>
      </div>
      {children}
    </section>
  );
}

/** The (i) stays on the title's last line: it is kept together with the last word. */
function TitleWithInfo({ title, info }: { readonly title: string; readonly info: ReactNode | undefined }): ReactNode {
  if (info === undefined) return title;
  const cut = title.lastIndexOf(' ');
  return (
    <>
      {cut === -1 ? null : `${title.slice(0, cut)} `}
      <span className="nowrap">
        {cut === -1 ? title : title.slice(cut + 1)}
        {'\u00a0'}
        <InfoTip>{info}</InfoTip>
      </span>
    </>
  );
}

function SectionState({ state, reading }: { readonly state: Section<unknown>; readonly reading: string }): ReactNode {
  if (state.kind === 'error') {
    return (
      <p className="error-banner" role="alert">
        {state.message}
      </p>
    );
  }
  return <p className="dash-loading">{reading}</p>;
}

// ---------------------------------------------------------------------------
// 1. Booking rules — read-only.
// ---------------------------------------------------------------------------

function refLabel(ref: ProviderRef): string | null {
  const text = [ref.code, ref.name].filter((part): part is string => part !== null && part !== '').join(' ');
  return text === '' ? null : text;
}

/** How many booking rules show before "Show all". */
export const BOOKING_RULES_FOLD = 5;

function BookingRulesSection({
  state,
  administrations,
  onNavigate,
}: {
  readonly state: Section<SupplierRules>;
  readonly administrations: readonly Administration[];
  readonly onNavigate: ((screen: ScreenId) => void) | undefined;
}): ReactNode {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const data = state.kind === 'ready' ? state.data : null;
  const unconfigured = (data?.administrations ?? []).filter((entry) => !entry.configured);
  const several = (data?.administrations.length ?? 0) > 1;

  // One flat list, each rule with the two lines it is drawn with, so the
  // search looks at exactly what the reviewer reads.
  const rows = (data?.administrations ?? []).flatMap((entry) =>
    entry.rules.map((rule, index) => {
      const relation = rule.relation === null ? null : refLabel(rule.relation);
      const supplier =
        rule.relation?.name ??
        rule.name ??
        relation ??
        (rule.partyType === 'P' ? t('rules.booking.oneOff') : t('rules.booking.unnamedRelation', { id: rule.relation?.id ?? '—' }));
      const ledger =
        rule.ledger === null
          ? entry.defaultLedger === null
            ? t('rules.booking.defaultUnknown')
            : t('rules.booking.default', {
                ledger: refLabel(entry.defaultLedger) ?? t('rules.booking.notSynced', { id: entry.defaultLedger.id }),
              })
          : t('rules.booking.always', { ledger: refLabel(rule.ledger) ?? t('rules.booking.notSynced', { id: rule.ledger.id }) });
      return { key: `${entry.administrationId}-${index}`, supplier, ledger, deductible: rule.vatDeductible };
    }),
  );
  const searchable = rows.length > BOOKING_RULES_FOLD;
  const needle = searchable ? search.trim().toLowerCase() : '';
  const matching = needle === '' ? rows : rows.filter((row) => `${row.supplier} ${row.ledger}`.toLowerCase().includes(needle));
  const shown = expanded ? matching : matching.slice(0, BOOKING_RULES_FOLD);

  return (
    <Block
      testId="booking-rules"
      icon="list"
      tone="violet"
      title={t('rules.booking.title')}
      info={t('rules.booking.info')}
    >
      {data === null ? (
        <SectionState state={state} reading={t('rules.booking.reading')} />
      ) : rows.length === 0 ? (
        <EmptyState
          testId="booking-rules-empty"
          icon="ledger"
          text={t('rules.booking.empty')}
          {...(onNavigate === undefined
            ? {}
            : { action: { label: t('rules.booking.openDocuments'), onClick: () => onNavigate('documents') } })}
        />
      ) : (
        <>
          {searchable ? (
            <div className="rule-search">
              <Icon name="search" size={15} />
              <input
                type="search"
                className="input"
                placeholder={t('rules.booking.search')}
                aria-label={t('rules.booking.search')}
                value={search}
                data-testid="booking-rules-search"
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          ) : null}
          {shown.map((row) => (
            <div className="rule-row" key={row.key} data-testid={`booking-rule-${row.key}`}>
              <div className="grow">
                <strong>{row.supplier}</strong>
                <p>{row.ledger}</p>
              </div>
              <span className={`badge ${row.deductible ? 'ok' : 'warn'}`}>
                {row.deductible ? t('rules.booking.deductible') : t('rules.booking.notDeductible')}
              </span>
            </div>
          ))}
          {matching.length === 0 ? (
            <p className="rule-foot" data-testid="booking-rules-nomatch">
              {t('rules.booking.noMatch', { text: search.trim() })}
            </p>
          ) : null}
          {matching.length > BOOKING_RULES_FOLD ? (
            <div className="rule-more">
              <button
                type="button"
                className="btn text"
                data-testid="booking-rules-more"
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? t('rules.booking.showLess') : t('rules.booking.showAll', { count: matching.length })}
              </button>
            </div>
          ) : null}
        </>
      )}
      {unconfigured.length === 0 || !several ? null : (
        <p className="rule-foot">
          {unconfigured
            .map((entry) => t('rules.booking.notConfigured', { name: administrationName(administrations, entry.administrationId) }))
            .join(' ')}
        </p>
      )}
    </Block>
  );
}

// ---------------------------------------------------------------------------
// 2. Counterparty questions.
// ---------------------------------------------------------------------------

function CounterpartySection({
  state,
  administrations,
  busy,
  onClassify,
  onOpenDocument,
}: {
  readonly state: Section<CounterpartyClassifications>;
  readonly administrations: readonly Administration[];
  readonly busy: boolean;
  readonly onClassify: (
    documentId: string,
    sourceVersion: number,
    classification: CounterpartyClassification,
  ) => void;
  readonly onOpenDocument: (documentId: string) => void;
}): ReactNode {
  const t = useT();
  const [open, setOpen] = useState<string | null>(null);
  const data = state.kind === 'ready' ? state.data : null;
  const waiting = (data?.pending ?? []).filter((entry) => entry.decided === null);
  const answered = (data?.pending ?? []).filter((entry) => entry.decided !== null);

  return (
    <Block
      icon="users"
      tone="amber"
      title={t('rules.cp.title')}
      info={t('rules.cp.info')}
    >
      {data === null ? (
        <SectionState state={state} reading={t('rules.cp.reading')} />
      ) : waiting.length === 0 && answered.length === 0 && data.decided.length === 0 ? (
        // Only when there is nothing AT ALL. An office with no open question
        // and a history of answers has something to show.
        <div className="rule-empty" data-testid="counterparties-clear">
          <strong>{t('rules.cp.empty')}</strong>
          <p>{t('rules.cp.emptyLine')}</p>
        </div>
      ) : (
        <>
          {waiting.map((entry) => (
            <div className="rule-row" key={entry.documentId} data-testid={`pending-${entry.documentId}`}>
              <div className="grow">
                <strong>{entry.party.printedName ?? t('rules.cp.noName')}</strong>
                <p className="muted small">
                  {t('rules.cp.arrived', {
                    administration: administrationName(administrations, entry.administrationId),
                    day: format.day(entry.receivedAt),
                  })}{' '}
                  ·{' '}
                  {entry.party.vatNumber === null && entry.party.registrationNumber === null
                    ? t('rules.cp.noNumbers')
                    : t('rules.cp.numbers', {
                        vat: entry.party.vatNumber ?? '—',
                        kvk: entry.party.registrationNumber ?? '—',
                      })}
                </p>
                {open === entry.documentId ? (
                  <ClassifyCounterparty
                    working={busy}
                    onClassify={(choice) => onClassify(entry.documentId, entry.sourceVersion, choice)}
                  />
                ) : null}
              </div>
              <div className="rule-actions">
                <button type="button" className="btn text" onClick={() => onOpenDocument(entry.documentId)}>
                  {t('rules.cp.open')}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setOpen(open === entry.documentId ? null : entry.documentId)}
                >
                  {open === entry.documentId ? t('common.close') : t('rules.cp.answer')}
                </button>
              </div>
            </div>
          ))}

          {answered.map((entry) => (
            <div className="rule-row" key={entry.documentId} data-testid={`answered-${entry.documentId}`}>
              <div className="grow">
                <strong>{entry.party.printedName ?? t('rules.cp.noName')}</strong>
                {/* Answered, and still listed. The blocker clears when the
                    document is read again; hiding it in between would make it
                    vanish from the only screen that lists it. */}
                <p className="muted small">{t('rules.cp.answered', { kind: entry.decided ?? '' })}</p>
              </div>
              <span className="badge blue">{t('rules.cp.answeredBadge')}</span>
            </div>
          ))}

          <div className="rule-sub">
            <h3>
              {t('rules.cp.decidedTitle')} <InfoTip>{t('rules.cp.decidedInfo')}</InfoTip>
            </h3>
            {data.decided.length === 0 ? (
              <p className="muted small">{t('rules.cp.decidedNone')}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('rules.cp.col.party')}</th>
                      <th>{t('rules.cp.col.matchedOn')}</th>
                      <th>{t('rules.cp.col.decided')}</th>
                      <th>{t('rules.cp.col.by')}</th>
                      <th>{t('rules.cp.col.when')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.decided.map((entry) => (
                      <tr key={entry.id} data-testid={`decided-${entry.id}`}>
                        <td>{entry.matchValue}</td>
                        <td>{entry.matchKind === 'vat_number' ? t('rules.cp.vatNumber') : t('rules.cp.name')}</td>
                        <td>
                          {entry.classification === 'one_off' ? t('rules.cp.oneOff') : t('rules.cp.recurring')}
                          {entry.isCurrent ? null : (
                            // Kept and flagged. Bookings were made on this
                            // answer, so it has to stay visible.
                            <>
                              {' '}
                              <span className="badge">{t('rules.cp.superseded')}</span>
                            </>
                          )}
                        </td>
                        <td className="muted small">{entry.actorUserId}</td>
                        <td className="muted small">{format.dateTime(entry.decidedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </Block>
  );
}

// ---------------------------------------------------------------------------
// 3. Intake routes.
// ---------------------------------------------------------------------------

function IntakeRouteSection({
  state,
  administrations,
  role,
  busy,
  mayApprove,
  currentUserId,
  onCreate,
  onRevoke,
}: {
  readonly state: Section<IntakeRoutes>;
  readonly administrations: readonly Administration[];
  readonly role: string;
  readonly busy: boolean;
  readonly mayApprove: boolean;
  readonly currentUserId: string;
  readonly onCreate: (body: Record<string, unknown>) => void;
  readonly onRevoke: (routeId: string) => void;
}): ReactNode {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [channel, setChannel] = useState<string>('mailbox');
  const [address, setAddress] = useState('');
  const [adminId, setAdminId] = useState(administrations[0]?.id ?? '');
  const [secretRef, setSecretRef] = useState('');

  const live = state.kind === 'ready' ? state.data.routes.filter((r) => r.revokedAt === null) : [];
  const revoked = state.kind === 'ready' ? state.data.routes.filter((r) => r.revokedAt !== null) : [];

  return (
    <Block
      testId="intake-routes"
      icon="mail"
      tone="green"
      title={t('rules.routes.title')}
      info={`${t('rules.routes.sub')}. ${t('rules.routes.info')}`}
      action={
        mayApprove ? (
          <button
            type="button"
            className="btn small head-add"
            onClick={() => setAdding(!adding)}
            data-testid="routes-add"
            aria-label={adding ? t('common.cancel') : t('rules.routes.add')}
          >
            {adding ? null : <Icon name="plus" size={14} />}
            {adding ? t('common.cancel') : t('rules.routes.add')}
          </button>
        ) : null
      }
    >
      {adding ? (
        <div className="rule-form" data-testid="route-form">
          <div className="form-row">
            <label className="field">
              {t('rules.routes.administration')}
              <select value={adminId} onChange={(event) => setAdminId(event.target.value)}>
                {administrations.map((administration) => (
                  <option key={administration.id} value={administration.id}>
                    {administration.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              {t('rules.routes.channel')}
              <select value={channel} onChange={(event) => setChannel(event.target.value)}>
                {CHANNELS.map((entry) => (
                  <option key={entry} value={entry}>
                    {channelLabel(entry, t)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              {t('rules.routes.address')}
              <input
                aria-label={t('rules.routes.addressLabel')}
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                placeholder="facturen.jansen@intake.example"
              />
            </label>
          </div>
          <p className="muted small">{t(`rules.channel.${channel}Detail` as MessageKey)}</p>
          {channel === 'webhook' ? (
            <label className="field">
              {t('rules.routes.secret')}
              <input
                aria-label={t('rules.routes.secret')}
                value={secretRef}
                onChange={(event) => setSecretRef(event.target.value)}
              />
              {/* A pointer, never the secret. The endpoint refuses a secret and
                  a credential in a request body is a credential in an access log. */}
              <span className="muted small">{t('rules.routes.secretHint')}</span>
            </label>
          ) : null}
          <button
            type="button"
            className="btn primary"
            disabled={busy || address.trim() === '' || adminId === ''}
            onClick={() => {
              onCreate({
                administrationId: adminId,
                channel,
                sourceAddress: address.trim(),
                // Whose authority documents from this route are processed
                // under. Defaulted to the approver, who by definition holds
                // every administration in the office.
                onBehalfOf: currentUserId,
                ...(channel === 'webhook' ? { signingSecretRef: secretRef.trim() } : {}),
              });
              setAdding(false);
              setAddress('');
              setSecretRef('');
            }}
          >
            {t('rules.routes.approve')}
          </button>
        </div>
      ) : null}

      {state.kind !== 'ready' ? (
        <SectionState state={state} reading={t('rules.routes.reading')} />
      ) : (
        <>
          {live.map((route) => {
            const name = route.channel === 'upload' ? t('rules.routes.uploadTitle') : route.sourceAddress;
            // On the row: what it is and where it goes. The rest — channel,
            // signing secret, approval date, revoking — is behind the "…".
            const details = [
              route.channel === 'upload' ? null : channelLabel(route.channel, t),
              route.channel === 'webhook'
                ? (route.hasSigningSecret === true ? t('rules.routes.secretOk') : t('rules.routes.secretMissing')).replace(/^ · /, '')
                : null,
              t('rules.routes.approvedOn', { day: format.day(route.approvedAt) }),
            ].filter((part): part is string => part !== null);
            return (
              <div className="rule-row route-row" key={route.id} data-testid={`route-${route.id}`}>
                <p className="grow route-line">
                  <strong>{name}</strong>
                  <span>{administrationName(administrations, route.administrationId)}</span>
                </p>
                <span className="pill green">{t('rules.routes.active')}</span>
                <RowMenu
                  label={t('rules.routes.more', { name })}
                  testId={`route-menu-${route.id}`}
                  notes={details}
                  items={
                    mayApprove
                      ? [{ label: t('rules.routes.revoke'), danger: true, disabled: busy, onSelect: () => onRevoke(route.id), testId: `route-revoke-${route.id}` }]
                      : []
                  }
                />
              </div>
            );
          })}
          {live.some((route) => route.channel === 'mailbox') ? null : (
            // The design's second row: forwarding by e-mail, before an address exists.
            <div className="rule-row" data-testid="routes-mail-unset">
              <div className="grow">
                <strong>{t('rules.routes.mailTitle')}</strong>
                <p>{t('rules.routes.mailUnset')}</p>
              </div>
              {mayApprove && !adding ? (
                <button
                  type="button"
                  className="btn text"
                  onClick={() => {
                    setChannel('mailbox');
                    setAdding(true);
                  }}
                >
                  {t('rules.routes.setUp')}
                </button>
              ) : null}
            </div>
          )}
        </>
      )}

      {revoked.length === 0 ? null : <p className="rule-foot">{t('rules.routes.revoked', { count: revoked.length })}</p>}
    </Block>
  );
}

// ---------------------------------------------------------------------------
// Bank and cash — read-only. GET /administrations/{id}/settings, which the
// worker reads from fiscal_config. Changed in Administrations (owner or agency
// operator), never here.
// ---------------------------------------------------------------------------

function BankAndCashSection({
  state,
  administrations,
}: {
  readonly state: Section<readonly AdministrationSettings[]>;
  readonly administrations: readonly Administration[];
}): ReactNode {
  const t = useT();
  const all = state.kind === 'ready' ? state.data : null;
  const several = (all?.length ?? 0) > 1;

  const ledger = (settings: AdministrationSettings, id: string | null): string => {
    if (id === null) return t('rules.bank.notSet');
    const found = settings.ledgers.find((entry) => entry.id === id);
    if (found === undefined) return t('rules.booking.notSynced', { id });
    return [found.code, found.name].filter((part): part is string => part !== null && part !== '').join(' ');
  };
  const delivery = (settings: AdministrationSettings): string => {
    const provider = administrations.find((entry) => entry.id === settings.administrationId)?.connection?.provider ?? null;
    const packageName = provider === null ? t('feed.package') : (PROVIDER_NAMES[provider] ?? provider);
    // No choice recorded means the worker's default, live_feed: say so rather than "not set".
    if (settings.bankDelivery === null) return t('rules.bank.defaultLiveFeed', { package: packageName });
    return settings.bankDelivery === 'mt940' ? t('rules.bank.mt940') : t('rules.bank.liveFeed', { package: packageName });
  };

  return (
    <Block testId="bank-and-cash" icon="swap" tone="blue" title={t('rules.bank.title')} info={t('rules.bank.info')}>
      {all === null ? (
        <SectionState state={state} reading={t('rules.bank.reading')} />
      ) : (
        all.map((settings) => (
          <dl className="rule-facts" key={settings.administrationId} data-testid={`bank-and-cash-${settings.administrationId}`}>
            {several ? <p className="rule-facts-title">{administrationName(administrations, settings.administrationId)}</p> : null}
            <div>
              <dt>{t('rules.bank.delivery')}</dt>
              <dd>{delivery(settings)}</dd>
            </div>
            <div>
              <dt>{t('rules.bank.cash')}</dt>
              <dd className={settings.cashLedgerId === null ? 'unset' : ''}>{ledger(settings, settings.cashLedgerId)}</dd>
            </div>
            <div>
              <dt>{t('rules.bank.settlement')}</dt>
              <dd className={settings.settlementLedgerId === null ? 'unset' : ''}>{ledger(settings, settings.settlementLedgerId)}</dd>
            </div>
          </dl>
        ))
      )}
    </Block>
  );
}

// ---------------------------------------------------------------------------
// 4. The routing queue.
// ---------------------------------------------------------------------------

function TriageSection({
  state,
  administrations,
  busy,
  mayAct,
  role,
  onFile,
  onDismiss,
}: {
  readonly state: Section<TriageQueue>;
  readonly administrations: readonly Administration[];
  readonly busy: boolean;
  readonly mayAct: boolean;
  readonly role: string;
  readonly onFile: (arrivalId: string, administrationId: string) => void;
  readonly onDismiss: (arrivalId: string, reason: string) => void;
}): ReactNode {
  const t = useT();
  const [choice, setChoice] = useState<Readonly<Record<string, string>>>({});
  const [reason, setReason] = useState<Readonly<Record<string, string>>>({});
  const data = state.kind === 'ready' ? state.data : null;

  // Without the triage grant this queue is not the user's to work, so the card
  // is not drawn at all (the API answers 200 with mayTriage: false, not an
  // error). While reading, nothing is drawn either, so it cannot flash up and
  // vanish. A failed read is still shown: that is not the same as no grant.
  if (state.kind === 'loading' || (data !== null && !data.mayTriage)) return null;

  return (
    <Block
      icon="inbox"
      tone="neutral"
      title={t('rules.triage.title')}
      info={data !== null && data.mayTriage && data.arrivals.length > 0 ? t('rules.triage.foot') : t('rules.triage.sub')}
    >
      {data === null ? (
        <SectionState state={state} reading={t('rules.triage.reading')} />
      ) : !data.mayTriage ? (
        // Not an error, and not an empty list: an empty queue and a queue you
        // may not see must not look alike.
        <EmptyState
          testId="triage-not-granted"
          icon="lock"
          text={t('rules.triage.notGranted')}
          info={<InfoTip>{t('rules.triage.notGrantedInfo')}</InfoTip>}
        />
      ) : data.arrivals.length === 0 ? (
        <EmptyState icon="done" text={t('rules.triage.empty')} info={<InfoTip>{t('rules.triage.emptyInfo')}</InfoTip>} />
      ) : (
        data.arrivals.map((arrival) => (
          <div className="rule-row" key={arrival.id} data-testid={`arrival-${arrival.id}`}>
            <div className="grow">
              <strong>{arrival.sourceAddress}</strong>
              <p className="muted small">
                {arrival.channel} ·{' '}
                {(UNROUTED as readonly string[]).includes(arrival.reason)
                  ? t(`rules.unrouted.${arrival.reason}` as MessageKey)
                  : arrival.reason}{' '}
                · {arrival.mediaType} · {Math.max(1, Math.round(arrival.byteSize / 1024))} kB ·{' '}
                {t('rules.triage.arrived', { when: format.dateTime(arrival.receivedAt) })}
              </p>
              {mayAct ? (
                <div className="form-row" style={{ marginTop: 8 }}>
                  <label className="field">
                    {t('rules.triage.fileInto')}
                    <select
                      aria-label={t('rules.triage.fileIntoLabel', { address: arrival.sourceAddress })}
                      value={choice[arrival.id] ?? administrations[0]?.id ?? ''}
                      onChange={(event) => setChoice({ ...choice, [arrival.id]: event.target.value })}
                    >
                      {administrations.map((administration) => (
                        <option key={administration.id} value={administration.id}>
                          {administration.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    {t('rules.triage.dismissReason')}
                    <input
                      aria-label={t('rules.triage.dismissLabel', { address: arrival.sourceAddress })}
                      value={reason[arrival.id] ?? ''}
                      onChange={(event) => setReason({ ...reason, [arrival.id]: event.target.value })}
                    />
                  </label>
                </div>
              ) : null}
            </div>
            {mayAct ? (
              <div className="rule-actions">
                <button
                  type="button"
                  className="btn"
                  disabled={busy || administrations.length === 0}
                  onClick={() => onFile(arrival.id, choice[arrival.id] ?? administrations[0]?.id ?? '')}
                >
                  {t('rules.triage.file')}
                </button>
                <button
                  type="button"
                  className="btn danger"
                  disabled={busy || (reason[arrival.id] ?? '').trim() === ''}
                  onClick={() => onDismiss(arrival.id, (reason[arrival.id] ?? '').trim())}
                >
                  {t('rules.triage.dismiss')}
                </button>
              </div>
            ) : (
              <span className="badge" title={t('rules.triage.roleCannot', { role })}>
                {t('rules.triage.readOnly')}
              </span>
            )}
          </div>
        ))
      )}
    </Block>
  );
}

// ---------------------------------------------------------------------------
// 5. Automation policies.
// ---------------------------------------------------------------------------

function PolicySection({
  state,
  administrations,
  administrationId,
  role,
  busy,
  onAction,
}: {
  readonly state: Section<PolicyVersions>;
  readonly administrations: readonly Administration[];
  readonly administrationId: string;
  readonly role: string;
  readonly busy: boolean;
  readonly onAction: (path: string, body: Record<string, unknown>, message: string) => void;
}): ReactNode {
  const t = useT();
  const [requesting, setRequesting] = useState(false);
  const [scope, setScope] = useState<string>(SCOPES[0]);
  const [amountLimit, setAmountLimit] = useState('');
  const [adminId, setAdminId] = useState(
    administrationId === ALL_ADMINISTRATIONS ? (administrations[0]?.id ?? '') : administrationId,
  );
  const versions = useMemo(() => (state.kind === 'ready' ? state.data.policyVersions : []), [state]);

  const mayRequest = POLICY_REQUESTER.has(role);
  const mayApprove = POLICY_APPROVER.has(role);
  const mayOperate = POLICY_OPERATOR.has(role);
  // The API decides this, never the screen. Nothing in this build reports true.
  const inForce = versions.some((version) => version.gates.release?.inForce === true);

  return (
    <section className="dash-card rule-block automation" data-testid="automation">
      <div className="auto-banner" data-testid="release-does-nothing">
        <span className="tile big blue" aria-hidden="true">
          <Icon name="lock" size={24} />
        </span>
        <div className="grow">
          <h2>{inForce ? t('rules.auto.onTitle') : t('rules.auto.offTitle')}</h2>
          <p>
            {inForce ? t('rules.auto.onLine') : t('rules.auto.offLine')} <InfoTip>{t('rules.auto.lockInfo')}</InfoTip>
          </p>
        </div>
        {mayRequest ? (
          <button
            type="button"
            className="btn head-btn"
            onClick={() => setRequesting(!requesting)}
            data-testid="scope-request"
            aria-label={requesting ? t('common.cancel') : t('rules.auto.request')}
          >
            {requesting ? t('common.cancel') : t('rules.auto.request')}
          </button>
        ) : null}
      </div>

      {requesting ? (
        <div className="rule-form" data-testid="policy-form">
          <div className="form-row">
            <label className="field">
              {t('rules.routes.administration')}
              <select value={adminId} onChange={(event) => setAdminId(event.target.value)}>
                {administrations.map((administration) => (
                  <option key={administration.id} value={administration.id}>
                    {administration.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              {t('rules.auto.scope')}
              <select value={scope} onChange={(event) => setScope(event.target.value)}>
                {SCOPES.map((entry) => (
                  <option key={entry} value={entry}>
                    {scopeLabel(entry, t)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              {t('rules.auto.amountLimit')}
              <input
                aria-label={t('rules.auto.amountLimitLabel')}
                value={amountLimit}
                onChange={(event) => setAmountLimit(event.target.value)}
                placeholder="250.00"
              />
            </label>
          </div>
          <p className="muted small">{t(`rules.scope.${scope}.detail` as MessageKey)}</p>
          <button
            type="button"
            className="btn primary"
            disabled={busy || adminId === ''}
            onClick={() => {
              onAction(
                `/administrations/${encodeURIComponent(adminId)}/policy-versions`,
                {
                  scope,
                  // Whatever the office asks for, stored and hashed. Nothing in
                  // this build interprets these, and a screen that validated a
                  // shape nothing executes would be inventing one.
                  rules: amountLimit.trim() === '' ? {} : { maximumAmount: amountLimit.trim() },
                },
                t('rules.auto.recorded'),
              );
              setRequesting(false);
              setAmountLimit('');
            }}
          >
            {t('rules.auto.record')}
          </button>
        </div>
      ) : null}

      {state.kind !== 'ready' ? (
        state.kind === 'error' ? <SectionState state={state} reading={t('rules.auto.reading')} /> : null
      ) : versions.length === 0 ? null : (
        versions.map((version) => (
          <PolicyRow
            key={version.id}
            version={version}
            administrations={administrations}
            busy={busy}
            mayApprove={mayApprove}
            mayOperate={mayOperate}
            onAction={onAction}
          />
        ))
      )}
    </section>
  );
}

function PolicyRow({
  version,
  administrations,
  busy,
  mayApprove,
  mayOperate,
  onAction,
}: {
  readonly version: PolicyVersion;
  readonly administrations: readonly Administration[];
  readonly busy: boolean;
  readonly mayApprove: boolean;
  readonly mayOperate: boolean;
  readonly onAction: (path: string, body: Record<string, unknown>, message: string) => void;
}): ReactNode {
  const t = useT();
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [form, setForm] = useState({
    connectorVersion: '',
    capabilityEvidenceRef: '',
    modelVersion: '',
    testDatasetRef: '',
    testResultsRef: '',
    casesTotal: '',
    casesPassed: '',
  });
  const next = version.gates.next;
  const known = next in GATE_TONE;
  const gate = {
    label: known ? t(`rules.gate.${next}` as MessageKey) : next,
    tone: GATE_TONE[next] ?? 'neutral',
    who: known ? t(`rules.gate.${next}.who` as MessageKey) : '',
  };
  const evidence = version.gates.evidence;
  const approval = version.gates.approval;
  const release = version.gates.release;

  return (
    <div className="rule-row" data-testid={`policy-${version.id}`}>
      <div className="grow">
        <strong>{t('rules.auto.version', { scope: scopeLabel(version.scope, t), version: version.version })}</strong>
        <p className="muted small">
          {t('rules.auto.askedFor', {
            administration: administrationName(administrations, version.administrationId),
            day: format.day(version.createdAt),
            hash: version.policyHash.slice(0, 12),
          })}
        </p>

        <dl className="kv" style={{ marginTop: 8 }}>
          <dt>{t('rules.auto.tested')}</dt>
          <dd>
            {evidence === null
              ? t('rules.auto.notYet')
              : [
                  t('rules.auto.cases', { passed: evidence.casesPassed, total: evidence.casesTotal }),
                  evidence.connectorVersion,
                  evidence.modelVersion,
                  evidence.testResultsRef,
                ]
                  .filter((part): part is string => part !== null)
                  .join(' · ')}
          </dd>
          <dt>{t('rules.auto.approvedBy')}</dt>
          <dd>
            {approval === null
              ? t('rules.auto.notApproved')
              : `${format.dateTime(approval.approvedAt)} · ${approval.approvedBy}`}
          </dd>
          <dt>{t('rules.auto.released')}</dt>
          <dd>
            {release === null ? (
              t('rules.auto.no')
            ) : (
              <>
                {t('rules.auto.boundTo', {
                  day: format.day(release.effectiveFrom),
                  versions:
                    release.modelVersion === null
                      ? release.connectorVersion
                      : t('rules.auto.and', { a: release.connectorVersion, b: release.modelVersion }),
                })}{' '}
                {/* The API decides this, not the screen: a release binds nothing
                    that runs today. */}
                {release.inForce ? t('rules.auto.inForce') : t('rules.auto.notInForce')}
              </>
            )}
          </dd>
        </dl>

        {evidenceOpen ? (
          <div className="rule-form" data-testid={`evidence-form-${version.id}`}>
            <div className="form-row">
              <EvidenceField label={t('rules.auto.connectorVersion')} aria={t('rules.auto.connectorVersion')} value={form.connectorVersion} onChange={(value) => setForm({ ...form, connectorVersion: value })} />
              <EvidenceField label={t('rules.auto.capabilityEvidence')} aria={t('rules.auto.capabilityEvidenceLabel')} value={form.capabilityEvidenceRef} onChange={(value) => setForm({ ...form, capabilityEvidenceRef: value })} />
              <EvidenceField label={t('rules.auto.modelVersion')} aria={t('rules.auto.modelVersionLabel')} value={form.modelVersion} onChange={(value) => setForm({ ...form, modelVersion: value })} />
            </div>
            <div className="form-row">
              <EvidenceField label={t('rules.auto.dataset')} aria={t('rules.auto.datasetLabel')} value={form.testDatasetRef} onChange={(value) => setForm({ ...form, testDatasetRef: value })} />
              <EvidenceField label={t('rules.auto.results')} aria={t('rules.auto.resultsLabel')} value={form.testResultsRef} onChange={(value) => setForm({ ...form, testResultsRef: value })} />
              <EvidenceField label={t('rules.auto.casesRun')} aria={t('rules.auto.casesRun')} value={form.casesTotal} onChange={(value) => setForm({ ...form, casesTotal: value })} />
              <EvidenceField label={t('rules.auto.casesPassed')} aria={t('rules.auto.casesPassed')} value={form.casesPassed} onChange={(value) => setForm({ ...form, casesPassed: value })} />
            </div>
            <button
              type="button"
              className="btn primary"
              disabled={busy}
              onClick={() => {
                onAction(
                  `/policy-versions/${encodeURIComponent(version.id)}/test-evidence`,
                  {
                    connectorVersion: form.connectorVersion.trim(),
                    capabilityEvidenceRef: form.capabilityEvidenceRef.trim(),
                    ...(form.modelVersion.trim() === '' ? {} : { modelVersion: form.modelVersion.trim() }),
                    testDatasetRef: form.testDatasetRef.trim(),
                    testResultsRef: form.testResultsRef.trim(),
                    casesTotal: Number(form.casesTotal),
                    casesPassed: Number(form.casesPassed),
                  },
                  t('rules.auto.evidenceRecorded'),
                );
                setEvidenceOpen(false);
              }}
            >
              {t('rules.auto.recordEvidence')}
            </button>
          </div>
        ) : null}
      </div>

      <div className="rule-actions">
        <span className={`badge ${gate.tone}`} title={gate.who}>
          {gate.label}
        </span>

        {next === 'test_evidence' && mayOperate ? (
          <button type="button" className="btn" onClick={() => setEvidenceOpen(!evidenceOpen)}>
            {evidenceOpen ? t('common.cancel') : t('rules.auto.recordTestEvidence')}
          </button>
        ) : null}

        {next === 'office_approval' && mayApprove && evidence !== null ? (
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={() =>
              onAction(
                `/policy-versions/${encodeURIComponent(version.id)}/office-approval`,
                {
                  // The exact evidence row and the exact policy hash. If either
                  // moved while this screen was open the API answers 409.
                  evidenceId: evidence.id,
                  policyHash: version.policyHash,
                },
                t('rules.auto.approvedNotice'),
              )
            }
          >
            {t('rules.auto.approveResults')}
          </button>
        ) : null}

        {next === 'release' && mayOperate ? (
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={() =>
              onAction(`/policy-versions/${encodeURIComponent(version.id)}/release`, {}, t('rules.auto.releasedNotice'))
            }
          >
            {t('rules.auto.release')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function EvidenceField({
  label,
  aria,
  value,
  onChange,
}: {
  readonly label: string;
  readonly aria: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): ReactNode {
  return (
    <label className="field">
      {label}
      <input aria-label={aria} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

// ---------------------------------------------------------------------------

function administrationName(administrations: readonly Administration[], id: string): string {
  // The id rather than "unknown": a reviewer whose grant does not cover this
  // administration still needs something they can quote when they ask about it.
  return administrations.find((administration) => administration.id === id)?.name ?? id;
}

function sectionMessage(caught: unknown, t: Translate): string {
  if (!(caught instanceof ApiError)) return t('common.notReadable');
  if (caught.code === 'transport_error') return t('rules.section.unreachable');
  if (caught.isAbsent) return t('rules.section.notAvailable');
  if (caught.status === 403) return t('rules.section.roleCannotRead');
  return t('common.notReadable');
}

function actionMessage(caught: unknown, t: Translate): string {
  if (!(caught instanceof ApiError)) return t('rules.action.failed');
  if (caught.isStale) return t('rules.action.stale');
  if (caught.status === 403) return t('rules.action.forbidden');
  if (caught.isAbsent) return t('rules.action.gone');
  if (caught.status === 422) {
    return t('rules.action.refused', {
      fields: caught.fieldErrors.map((f) => `${f.field} (${f.code})`).join(', ') || t('rules.action.validation'),
    });
  }
  return caught.message;
}
